import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Scale, Shield, AlertTriangle } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-full bg-blue-100 text-blue-600 mb-4">
              <Scale className="h-8 w-8" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-600">
              Last updated: January 2024
            </p>
          </div>

          {/* Important Notice */}
          <Alert className="mb-8 border-orange-200 bg-orange-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-orange-800">
              <strong>Important:</strong> Addiction Rehab Call is a directory and affiliate service. 
              We connect individuals with treatment facilities but do not provide medical treatment, 
              therapy, or addiction recovery services directly.
            </AlertDescription>
          </Alert>

          <Card className="shadow-lg">
            <CardContent className="p-8 prose max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 mb-6">
                By accessing and using addictionrehabcall.com (the "Website"), you accept and agree to be bound by 
                the terms and provision of this agreement. If you do not agree to abide by the above, please do not 
                use this service.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Nature of Our Service</h2>
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">We Are a Directory Service</h3>
                <ul className="text-blue-800 space-y-1">
                  <li>• We maintain a directory of addiction treatment facilities</li>
                  <li>• We provide information and referral services</li>
                  <li>• We may receive compensation from facilities for referrals</li>
                  <li>• We do not provide medical treatment or therapy services</li>
                  <li>• We are not licensed healthcare providers</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Use of Information</h2>
              <p className="text-gray-700 mb-4">
                The information provided on this website is for informational purposes only and should not be 
                considered as medical advice, diagnosis, or treatment recommendations. Always consult with 
                qualified healthcare professionals before making decisions about addiction treatment.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Facility Listings</h2>
              <p className="text-gray-700 mb-4">
                We strive to maintain accurate information about treatment facilities, but we cannot guarantee 
                the completeness, accuracy, or timeliness of all information. Facilities are responsible for 
                providing accurate information about their services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Affiliate Relationships</h2>
              <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-yellow-900 mb-2">Financial Disclosure</h3>
                <p className="text-yellow-800">
                  We may receive compensation from treatment facilities when users contact them through our website. 
                  This compensation helps us maintain and improve our services. However, compensation does not 
                  influence our listing criteria or recommendations.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. User Responsibilities</h2>
              <p className="text-gray-700 mb-4">You agree to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-1">
                <li>Use the website for lawful purposes only</li>
                <li>Provide accurate information when submitting forms</li>
                <li>Not attempt to harm or disrupt our services</li>
                <li>Respect the intellectual property rights of others</li>
                <li>Verify treatment facility credentials independently</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Facility Registration Terms</h2>
              <p className="text-gray-700 mb-4">
                Treatment facilities submitting information to our directory agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-1">
                <li>Provide accurate and current information</li>
                <li>Maintain appropriate licenses and certifications</li>
                <li>Update their information promptly when changes occur</li>
                <li>Comply with all applicable healthcare regulations</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimers and Limitations</h2>
              <div className="bg-red-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-red-900 mb-2">Important Disclaimers</h3>
                <ul className="text-red-800 space-y-1">
                  <li>• We make no warranties about treatment outcomes</li>
                  <li>• We are not responsible for the quality of care provided by facilities</li>
                  <li>• Users must verify facility credentials and services independently</li>
                  <li>• We are not liable for treatment decisions or medical outcomes</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Privacy</h2>
              <p className="text-gray-700 mb-6">
                Your privacy is important to us. Please review our{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>{' '}
                to understand how we collect, use, and protect your information.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Emergency Situations</h2>
              <div className="bg-red-100 p-4 rounded-lg mb-6 border border-red-200">
                <h3 className="font-semibold text-red-900 mb-2">Medical Emergencies</h3>
                <p className="text-red-800">
                  If you are experiencing a medical emergency or having thoughts of self-harm, 
                  do not use this website. Instead, call 911, go to your nearest emergency room, 
                  or call the 988 Suicide & Crisis Lifeline immediately.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Modifications to Terms</h2>
              <p className="text-gray-700 mb-6">
                We reserve the right to modify these terms at any time. Changes will be effective 
                immediately upon posting to the website. Your continued use of the website constitutes 
                acceptance of any modifications.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Addiction Rehab Call</strong><br />
                  Email: legal@addictionrehabcall.com<br />
                  Website: addictionrehabcall.com<br />
                  Phone: 1-888-968-1014
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;